import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../app/store';
import {
  fetchSessionsPaged,
  deleteSession,
  upsertSessionLocal,
  recomputeSessionAggregates,
} from '../app/slices/sessionSlice';
import { addToast } from '../app/slices/uiSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import { PlayIcon, StopIcon, PencilIcon, TrashIcon, ClockIcon } from '@heroicons/react/24/outline';
import { gstUtils } from '../lib/calculateGST';
import InlinePanel from '../ui/common/InlinePanel';
import { track } from '../lib/firebase';
import { trackUsage, startDwellTimer } from '../lib/telemetry';
import StartSessionForm from '../ui/sessions/StartSessionForm';
import EndSessionForm from '../ui/sessions/EndSessionForm';
import EditSessionForm from '../ui/sessions/EditSessionForm';
import {
  flag,
  idFromFlag,
  getPageFromUrl,
  setPageInUrl,
  focusRef as focusRefUtil,
} from '../lib/urlState';

const Sessions: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { sessions, currentSession, isSessionActive, isLoading } = useSelector(
    (state: RootState) => state.sessions
  );

  const startBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const endBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const editId = idFromFlag('edit');
  const isEditOpen = Boolean(editId);
  const startOpen = flag('start');
  const endOpen = flag('end');

  const [page, setPage] = useState(getPageFromUrl());
  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchSessionsPaged({ userId: user.uid, pageSize: 10 }));
    }
  }, [dispatch, user?.uid]);

  useEffect(() => {
    // Track page view and dwell time
    trackUsage('usage_page_view', { page: 'Sessions' }).catch(console.warn);
    const stop = startDwellTimer('Sessions');
    return stop;
  }, []);

  const openStart = () => {
    trackUsage('usage_action', { page: 'Sessions', action: 'start_session' }).catch(console.warn);
    const p = new URLSearchParams(location.search);
    p.set('start', '1');
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: false });
  };
  const openEnd = () => {
    trackUsage('usage_action', { page: 'Sessions', action: 'end_session' }).catch(console.warn);
    const p = new URLSearchParams(location.search);
    p.set('end', '1');
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: false });
  };
  const openEdit = (id: string) => {
    const p = new URLSearchParams(location.search);
    p.set('edit', id);
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: false });
  };
  const closeAny = () => {
    const p = new URLSearchParams(location.search);
    ['start', 'end', 'edit'].forEach(k => p.delete(k));
    const qs = p.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  };

  // start/end/edit handled in dedicated forms within InlinePanel

  const handleDeleteSession = async (sessionId: string) => {
    if (!user?.uid) return;

    if (
      window.confirm('Are you sure you want to delete this session? This action cannot be undone.')
    ) {
      try {
        await dispatch(deleteSession({ userId: user.uid, sessionId }));
        dispatch(
          addToast({
            message: 'Session deleted successfully',
            type: 'success',
          })
        );
      } catch {
        dispatch(
          addToast({
            message: 'Failed to delete session',
            type: 'error',
          })
        );
      }
    }
  };

  const formatDuration = (session: any) => {
    // Use user-adjusted duration if available, otherwise calculate from timestamps
    if (session.drivingDurationMinutes) {
      const hours = Math.floor(session.drivingDurationMinutes / 60);
      const minutes = session.drivingDurationMinutes % 60;
      return `${hours}h ${minutes}m`;
    } else {
      // Fallback to auto-calculated duration
      if (!session.startTime || !session.endTime) {
        return 'N/A';
      }
      const start = session.startTime.toDate
        ? session.startTime.toDate()
        : new Date(session.startTime);
      const end = session.endTime.toDate ? session.endTime.toDate() : new Date(session.endTime);
      const duration = end.getTime() - start.getTime();
      const hours = Math.floor(duration / (1000 * 60 * 60));
      const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}h ${minutes}m`;
    }
  };

  if (isLoading && !(startOpen || endOpen || isEditOpen)) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading sessions..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Branding Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-md"
      >
        <div className="flex items-center justify-center space-x-3 mb-2">
          <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            AutoTaxPilot™
          </h1>
        </div>
        <p className="text-center text-sm text-gray-600 font-medium">
          Better Return for Your Time • Accurate Tracking • Simple Interface
        </p>
      </motion.div>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl font-bold leading-7 text-gray-900 sm:text-4xl sm:truncate"
          >
            Driving Sessions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-base text-gray-600"
          >
            Track your driving sessions and earnings
          </motion.p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          {isSessionActive ? (
            <button
              ref={endBtnRef}
              onClick={() => {
                openEnd();
                track('panel_opened', { panel: 'session_end' });
              }}
              className="btn-warning"
              data-ref="endSessionBtn"
            >
              <StopIcon className="h-4 w-4 mr-2" />
              End Session
            </button>
          ) : (
            <button
              ref={startBtnRef}
              onClick={() => {
                openStart();
                track('panel_opened', { panel: 'session_start' });
              }}
              className="btn-success"
              data-ref="startSessionBtn"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              Start Session
            </button>
          )}
        </div>
      </div>

      {/* Current Session Status */}
      {isSessionActive && currentSession && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-success-50 border-success-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-success-500 rounded-full flex items-center justify-center">
                  <PlayIcon className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-success-900">Session Active</h3>
                <p className="text-sm text-success-700">
                  Started at odometer: {currentSession.startOdometer?.toLocaleString()} km
                </p>
              </div>
            </div>
            <button ref={endBtnRef} onClick={() => openEnd()} className="btn-warning">
              <StopIcon className="h-4 w-4 mr-2" />
              End Session
            </button>
          </div>
        </motion.div>
      )}

      {/* 1) PANEL */}
      <InlinePanel
        open={startOpen || endOpen || isEditOpen}
        title={
          startOpen ? 'Start Driving Session' : endOpen ? 'End Driving Session' : 'Edit Session'
        }
        subtitle={startOpen ? 'Enter your current odometer to start tracking.' : undefined}
        onClose={closeAny}
        className="max-w-2xl mx-auto"
        returnFocusRef={
          (startOpen
            ? (startBtnRef as any)
            : endOpen
              ? (endBtnRef as any)
              : undefined) as React.RefObject<HTMLElement>
        }
        regionId="session-inline-panel-title"
      >
        {startOpen && (
          <StartSessionForm
            onDone={() => {
              closeAny();
              focusRefUtil('startSessionBtn');
            }}
          />
        )}
        {endOpen && (
          <EndSessionForm
            onCancel={closeAny}
            onSaved={(saved: any) => {
              dispatch(upsertSessionLocal(saved));
              dispatch(recomputeSessionAggregates());
              closeAny();
              focusRefUtil('endSessionBtn');
            }}
          />
        )}
        {isEditOpen && (
          <EditSessionForm
            sessionId={editId!}
            onCancel={closeAny}
            onSaved={(saved: any) => {
              dispatch(upsertSessionLocal(saved));
              dispatch(recomputeSessionAggregates());
              closeAny();
            }}
          />
        )}
      </InlinePanel>

      {/* 2) SUMMARY */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
            <p className="text-sm text-gray-500">Total Sessions</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {sessions.reduce((sum, session) => sum + session.totalKmDriven, 0).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">Total KM Driven</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {gstUtils.formatCurrency(
                sessions.reduce((sum, session) => sum + session.grossEarnings, 0)
              )}
            </p>
            <p className="text-sm text-gray-500">Total Earnings</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {gstUtils.formatCurrency(
                sessions.reduce((sum, session) => sum + session.gstCollected, 0)
              )}
            </p>
            <p className="text-sm text-gray-500">GST Collected</p>
          </div>
        </div>
      </motion.div>

      {/* 3) LIST */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start your first driving session to begin tracking earnings and expenses.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Distance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Earnings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GST Collected
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session, index) => (
                  <motion.tr
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {session.startTime
                            ? (() => {
                                const timestamp = session.startTime.toDate
                                  ? session.startTime.toDate()
                                  : new Date(session.startTime as any);
                                return timestamp.toLocaleDateString('en-CA');
                              })()
                            : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">{formatDuration(session)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {session.totalKmDriven.toLocaleString()} km total
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.totalKmWithPassenger.toLocaleString()} km with passenger
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {gstUtils.formatCurrency(session.grossEarnings)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {gstUtils.formatCurrency(session.gstCollected)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEdit(session.id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit Session"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session.id)}
                          className="text-danger-600 hover:text-danger-900"
                          title="Delete Session"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paging controls */}
        {sessions.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 border-t mt-4">
            <div className="text-xs text-gray-500">Page {page}</div>
            <div className="flex gap-2">
              <button
                className="btn-secondary btn-sm"
                disabled={page === 1}
                onClick={() => {
                  // For simplicity, reload first page
                  if (!user?.uid) return;
                  setPage(1);
                  dispatch(fetchSessionsPaged({ userId: user.uid, pageSize: 10 }));
                  setPageInUrl(1);
                }}
              >
                Prev
              </button>
              <button
                className="btn-secondary btn-sm"
                onClick={() => {
                  if (!user?.uid) return;
                  // Use last item as cursor
                  const last = sessions[sessions.length - 1];
                  if (last.startTime) {
                    const cursor = {
                      startTimeMs: last.startTime.toDate
                        ? last.startTime.toDate().getTime()
                        : new Date(last.startTime as any).getTime(),
                      endTimeMs: last.endTime
                        ? last.endTime.toDate
                          ? last.endTime.toDate().getTime()
                          : new Date(last.endTime as any).getTime()
                        : null,
                    };
                    dispatch(fetchSessionsPaged({ userId: user.uid, pageSize: 10, after: cursor }));
                  }
                  const nextPage = page + 1;
                  setPage(nextPage);
                  setPageInUrl(nextPage);
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Sessions;
